// Кольцо месячного бюджета.
//
// Показывает не одну величину, а две сразу: сколько конверта потрачено (дуга)
// и сколько месяца прошло (засечка на окружности). Из их взаимного положения
// сразу видно то, ради чего экран и открывают: иду с опережением или отстаю.
// Одна дуга без засечки такого не сообщает — «потрачено 60%» само по себе
// ничего не значит, пока не знаешь, 60% месяца прошло или 20%.
//
// Дуга доезжает до значения за один проход при появлении: движение здесь
// объясняет, откуда взялось число, а не украшает. При prefers-reduced-motion
// кольцо просто отрисовывается на месте.

import { useEffect, useRef, useState } from 'react';

type Props = {
  /** Доля потраченного от плана, 0..1+ (может быть больше 1 при перерасходе). */
  spentRatio: number;
  /** Доля прошедшего месяца, 0..1. */
  timeRatio: number;
  size?: number;
  stroke?: number;
  children?: React.ReactNode;
};

function toneFor(spent: number, _time: number): string {
  // Цветом отмечается только настоящий выход за план. Опережение темпа видно
  // и без перекраски: дуга просто уходит дальше засечки времени — в этом весь
  // смысл засечки. Красить ещё и цветом значит кричать там, где достаточно
  // показать, и на тёмном фоне сплошной оранжевый читается как авария.
  return spent > 1 ? 'over' : 'ok';
}

export function BudgetRing({
  spentRatio,
  timeRatio,
  size = 132,
  stroke = 10,
  children,
}: Props): JSX.Element {
  const clamped = Math.max(0, Math.min(1, spentRatio));
  const [shown, setShown] = useState(0);
  const raf = useRef<number | null>(null);

  useEffect(() => {
    const reduce =
      typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setShown(clamped);
      return;
    }
    const start = performance.now();
    const dur = 620;
    const tick = (now: number): void => {
      const t = Math.min(1, (now - start) / dur);
      // easeOutCubic: быстрый старт, мягкая остановка — дуга «доезжает».
      setShown(clamped * (1 - (1 - t) ** 3));
      if (t < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => {
      if (raf.current != null) cancelAnimationFrame(raf.current);
    };
  }, [clamped]);

  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const tone = toneFor(spentRatio, timeRatio);
  // Засечка времени: угол от 12 часов по часовой стрелке.
  const angle = 2 * Math.PI * Math.max(0, Math.min(1, timeRatio)) - Math.PI / 2;
  const tickOuter = r + stroke / 2 + 3;
  const tickInner = r - stroke / 2 - 3;
  const cx = size / 2;
  const cy = size / 2;

  return (
    <div className="ring" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <title>Потрачено от плана месяца</title>
        <circle className="ring-track" cx={cx} cy={cy} r={r} fill="none" strokeWidth={stroke} />
        <circle
          className={`ring-arc tone-${tone}`}
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - shown)}
          transform={`rotate(-90 ${cx} ${cy})`}
        />
        <line
          className="ring-tick"
          x1={cx + tickInner * Math.cos(angle)}
          y1={cy + tickInner * Math.sin(angle)}
          x2={cx + tickOuter * Math.cos(angle)}
          y2={cy + tickOuter * Math.sin(angle)}
        />
      </svg>
      <div className="ring-center">{children}</div>
    </div>
  );
}
